/**
 * 售后模块 UI 工具（下拉/日期面板挂载，避免选项「漂移」错位）
 */
import './aftersalePopup.css'

/**
 * Select / Dropdown / Cascader 统一挂到 body，
 * 避免页面滚动容器、Modal 内层 overflow、水印 transform 导致下拉错位。
 */
export function getModalPopupContainer(triggerNode) {
  if (typeof document !== 'undefined' && document.body) {
    return document.body
  }
  if (triggerNode && triggerNode.parentNode) {
    return triggerNode.parentNode
  }
  return triggerNode
}

/** DatePicker 使用 getCalendarContainer，行为与 Select 一致 */
export function getModalCalendarContainer(triggerNode) {
  return getModalPopupContainer(triggerNode)
}

/** 下拉面板 class，保证盖过页面固定底栏/水印 */
export var AFTERSALE_DROPDOWN_CLASS = 'as-select-dropdown'

/** 详情页新开浏览器窗口，当前页保持不动 */
export function openAftersalePage (path, query) {
  var parts = []
  var q = query || {}
  Object.keys(q).forEach(function (key) {
    if (q[key] === undefined || q[key] === null || q[key] === '') return
    parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(q[key]))
  })
  var url = path + (parts.length ? ('?' + parts.join('&')) : '')
  window.open(url, '_blank')
}

/** 至今减去申请时间，精确到 0.1 天 */
export function cycleDaysText (applyTime) {
  if (!applyTime) return '-'
  var start = new Date(String(applyTime).replace(/-/g, '/'))
  if (isNaN(start.getTime())) return '-'
  var days = (Date.now() - start.getTime()) / 86400000
  if (days < 0) days = 0
  return (Math.round(days * 10) / 10).toFixed(1) + '天'
}

/** 站内上一页路径（用于返回：从哪进回哪） */
var PREV_ROUTE_KEY = '__css_prev_route'
var BLOCKED_BACK_PATHS = [
  '/login',
  '/register',
  '/404',
  '/403',
  '/resetPasswordByEmail',
  '/resetPasswordByMobile',
  '/overdueReset'
]

function getPathOnly(fullPath) {
  if (!fullPath) return ''
  return String(fullPath).split('?')[0]
}

function isSafeBackPath(fullPath) {
  var path = getPathOnly(fullPath)
  if (!path || path.charAt(0) !== '/') return false
  return BLOCKED_BACK_PATHS.indexOf(path) === -1
}

/**
 * 记录站内来源页。在 router.afterEach 中调用。
 * 门户 SSO 直达详情时通常无有效来源（或来源为登录页），返回时走兜底。
 */
export function rememberPreviousRoute(from) {
  if (!from || !from.matched || !from.matched.length) return
  if (!isSafeBackPath(from.fullPath || from.path)) return
  try {
    sessionStorage.setItem(PREV_ROUTE_KEY, from.fullPath || from.path)
  } catch (e) {
    /* ignore */
  }
}

function takePreviousRoute(currentFullPath) {
  var prev = null
  try {
    prev = sessionStorage.getItem(PREV_ROUTE_KEY)
  } catch (e) {
    prev = null
  }
  if (!prev || !isSafeBackPath(prev)) return null
  if (currentFullPath && prev === currentFullPath) return null
  if (getPathOnly(prev) === getPathOnly(currentFullPath)) return null
  try {
    sessionStorage.removeItem(PREV_ROUTE_KEY)
  } catch (e) {
    /* ignore */
  }
  return prev
}

/**
 * 返回列表/父级页面。
 * 1. 有站内来源（列表/待办等）→ 回到来源页
 * 2. 统一门户直达 / 无有效来源 → route.meta.parent 或 fallbackPath
 * 避免门户 SSO 场景 history.back() 回到登录中间页。
 */
export function goBackOrFallback(router, route, fallbackPath) {
  var prev = takePreviousRoute(route && route.fullPath)
  if (prev) {
    router.replace(prev)
    return
  }
  var parent = route && route.meta && route.meta.parent
  var target = parent || fallbackPath
  if (target) {
    router.replace(target)
    return
  }
  if (window.history.length > 1) {
    router.back()
  }
}

function parseDateTimeMs(value) {
  if (!value) return 0
  var text = String(value).trim()
  if (!text) return 0
  var ms = Date.parse(text.replace(/-/g, '/'))
  return isNaN(ms) ? 0 : ms
}

/** 待办项时间戳（startTime 为流转到当前步骤的时间） */
export function getTodoTimeMs(item) {
  if (!item) return 0
  return parseDateTimeMs(item.startTime || item.applyTime)
}

/** 待办列表按时间倒序（最新在前） */
export function sortTodoByTimeDesc(list) {
  return (list || []).slice().sort(function (a, b) {
    return getTodoTimeMs(b) - getTodoTimeMs(a)
  })
}

/** 只展示接口下发的临期状态，不在前端重算阈值 */
export function dueTagText (dueStatus) {
  if (dueStatus === 'OVERDUE') return '超期'
  if (dueStatus === 'NEARING') return '临期'
  return ''
}

export function dueTagColor (dueStatus) {
  if (dueStatus === 'OVERDUE') return 'red'
  if (dueStatus === 'NEARING') return 'orange'
  return ''
}

export function dueRowClass (record) {
  if (!record) return ''
  if (record.dueStatus === 'OVERDUE') return 'as-due-overdue'
  if (record.dueStatus === 'NEARING') return 'as-due-nearing'
  if (record.urgentHandle) return 'as-due-urgent'
  return ''
}

export function remainDaysText (remainDays) {
  if (remainDays === null || remainDays === undefined || remainDays === '') return '-'
  return String(remainDays)
}

/** 超期 > 临期 = 加急 > 其它；同档仍按时间倒序 */
export function dueSortRank (record) {
  if (!record) return 2
  if (record.dueStatus === 'OVERDUE') return 0
  if (record.dueStatus === 'NEARING' || record.urgentHandle) return 1
  return 2
}

export function sortDueThenTime (list) {
  return (list || []).slice().sort(function (a, b) {
    var rank = dueSortRank(a) - dueSortRank(b)
    if (rank !== 0) return rank
    return getTodoTimeMs(b) - getTodoTimeMs(a)
  })
}



// WEBPACK FOOTER //
// ./src/views/modules/afterSale/aftersale/utils/aftersaleUiUtils.js